import React, { useCallback, useEffect, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    Paper,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import WarningIcon from '@mui/icons-material/Warning';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PersonIcon from '@mui/icons-material/Person';
import InfoIcon from '@mui/icons-material/Info';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import MessageIcon from '@mui/icons-material/Message';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import {
    DataGridPremium,
    GridColDef,
    GridPaginationModel,
    GridSortModel,
} from '@mui/x-data-grid-premium';
import type { GridColumnGroupingModel } from '@mui/x-data-grid';
import { AdvancedOrganization } from '../networking.types';
import { useNetworkingOrganization } from '../state/NetworkingContext';
import {
    GetDashboardOrganizationParams,
    getOrgDashboardMetrics,
    getTierOneOrgs,
} from '../networking.api';
import { EntityType, Organization } from '../../../utils';
import debounce from 'lodash/debounce';

const ORG_METRICS_COLUMN_GROUPING_MODEL: GridColumnGroupingModel = [
    {
        groupId: 'subscription_status',
        headerName: 'Subscription Status',
        headerAlign: 'center',
        children: [{ field: 'subscription_level' }, { field: 'amount_past_due' }],
    },
    {
        groupId: 'product_adoption',
        headerName: 'Product Adoption',
        headerAlign: 'center',
        children: [
            { field: 'uploads_last_30_days' },
            { field: 'uploads_last_60_days' },
            { field: 'uploads_last_90_days' },
        ],
    },
    {
        groupId: 'commissions',
        headerName: 'Commissions',
        headerAlign: 'center',
        children: [
            { field: 'total_commissions' },
            { field: 'marketplace_commissions' },
            { field: 'pib_bonus_commissions' },
            { field: 'residual_commissions' },
            { field: 'check_match_commissions' },
        ],
    },
    {
        groupId: 'pod_allocations',
        headerName: 'Pod Allocations and Subscriptions',
        headerAlign: 'center',
        children: [
            { field: 'pods_org' },
            { field: 'org_pod_subscriptions' },
            { field: 'pods_athlete' },
            { field: 'athlete_pod_subscriptions' },
        ],
    },
];

const DashboardMetricsScene = () => {
    const theme = useTheme();
    const organization = useNetworkingOrganization();
    const [organizations, setOrganizations] = useState<AdvancedOrganization[]>([]);
    // Separate state for all organizations (for accurate counts, not affected by pagination)
    const [allOrganizations, setAllOrganizations] = useState<AdvancedOrganization[]>([]);
    const [searchParams, setSearchParams] = useState<GetDashboardOrganizationParams>({
        sort: '-uploads_last_30_days',
        page: 1,
        per_page: 50,
    });
    const [sortModel, setSortModel] = useState<GridSortModel>([
        {
            field: 'uploads_last_30_days',
            sort: 'desc',
        },
    ]);
    const [totalOrganizations, setTotalOrganizations] = useState(0);
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 50,
    });
    const [isLoading, setIsLoading] = useState(true);
    const [hasCompletedInitialLoad, setHasCompletedInitialLoad] = useState(false);
    const [orgOptions, setOrgOptions] = useState<Organization[]>([]);
    const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedOrganization, setSelectedOrganization] = useState<AdvancedOrganization | null>(
        null,
    );
    // Filter state for past due cards
    const [activeFilter, setActiveFilter] = useState<
        | 'gold-past-due'
        | 'silver-past-due'
        | 'bronze-past-due'
        | 'cancelled-past-due'
        | 'all-past-due'
        | null
    >(null);

    const handlePaginationModelChange = (newModel: GridPaginationModel) => {
        setPaginationModel(newModel);
        setSearchParams({
            ...searchParams,
            page: newModel.page + 1,
            per_page: newModel.pageSize,
        });
    };

    // Fetch all organizations for accurate counts (not affected by pagination)
    useEffect(() => {
        if (!organization?.data?.uuid) {
            return;
        }

        // Fetch all organizations with a high per_page limit for counting
        getOrgDashboardMetrics(organization.data.uuid, {
            sort: '-uploads_last_30_days',
            page: 1,
            per_page: 10000, // High limit to get all organizations for counting
        })
            .then((response) => {
                const realOrgs = response.data.data;

                // DUMMY DATA FOR LOCAL TESTING - REMOVE BEFORE PRODUCTION
                const dummyOrgs: AdvancedOrganization[] = [
                    // Gold orgs with past due
                    {
                        id: 9001,
                        uuid: 'dummy-gold-past-due-1',
                        name: 'Elite Gold Training',
                        slug: 'elite-gold-training',
                        about_us: null,
                        logo_url: null,
                        banner_url: null,
                        video_url: null,
                        mux_playback_id: undefined,
                        image_urls: {} as any,
                        object: EntityType.ORGANIZATION,
                        subscription_tier: 'gold',
                        subscription_tier_sort: 3,
                        amount_past_due: 260000, // $2,600.00
                        total_commissions: 0,
                        marketplace_commissions: 0,
                        pib_bonus_commissions: 0,
                        residual_commissions: 0,
                        check_match_commissions: 0,
                        uploads_last_30_days: 0,
                        uploads_last_60_days: 0,
                        uploads_last_90_days: 0,
                        athlete_count: 0,
                        subscriptions: [],
                        partner: {} as any,
                        creator: {
                            first_name: 'John',
                            last_name: 'Gold',
                            email: 'j.gold@elite.com',
                            phone: '555-0101',
                        } as any,
                    },
                    {
                        id: 9002,
                        uuid: 'dummy-gold-past-due-2',
                        name: 'Premium Gold Sports',
                        slug: 'premium-gold-sports',
                        about_us: null,
                        logo_url: null,
                        banner_url: null,
                        video_url: null,
                        mux_playback_id: undefined,
                        image_urls: {} as any,
                        object: EntityType.ORGANIZATION,
                        subscription_tier: 'gold',
                        subscription_tier_sort: 3,
                        amount_past_due: 260000, // $2,600.00
                        total_commissions: 0,
                        marketplace_commissions: 0,
                        pib_bonus_commissions: 0,
                        residual_commissions: 0,
                        check_match_commissions: 0,
                        uploads_last_30_days: 0,
                        uploads_last_60_days: 0,
                        uploads_last_90_days: 0,
                        athlete_count: 0,
                        subscriptions: [],
                        partner: {} as any,
                        creator: {
                            first_name: 'Sarah',
                            last_name: 'Gold',
                            email: 's.gold@premium.com',
                            phone: '555-0102',
                        } as any,
                    },
                    // Gold orgs without past due
                    {
                        id: 9003,
                        uuid: 'dummy-gold-no-past-due-1',
                        name: 'Gold Standard Athletics',
                        slug: 'gold-standard-athletics',
                        about_us: null,
                        logo_url: null,
                        banner_url: null,
                        video_url: null,
                        mux_playback_id: undefined,
                        image_urls: {} as any,
                        object: EntityType.ORGANIZATION,
                        subscription_tier: 'gold',
                        subscription_tier_sort: 3,
                        amount_past_due: 0,
                        total_commissions: 0,
                        marketplace_commissions: 0,
                        pib_bonus_commissions: 0,
                        residual_commissions: 0,
                        check_match_commissions: 0,
                        uploads_last_30_days: 0,
                        uploads_last_60_days: 0,
                        uploads_last_90_days: 0,
                        athlete_count: 0,
                        subscriptions: [],
                        partner: {} as any,
                        creator: {} as any,
                    },
                    {
                        id: 9004,
                        uuid: 'dummy-gold-no-past-due-2',
                        name: 'Top Gold Performance',
                        slug: 'top-gold-performance',
                        about_us: null,
                        logo_url: null,
                        banner_url: null,
                        video_url: null,
                        mux_playback_id: undefined,
                        image_urls: {} as any,
                        object: EntityType.ORGANIZATION,
                        subscription_tier: 'gold',
                        subscription_tier_sort: 3,
                        amount_past_due: 0,
                        total_commissions: 0,
                        marketplace_commissions: 0,
                        pib_bonus_commissions: 0,
                        residual_commissions: 0,
                        check_match_commissions: 0,
                        uploads_last_30_days: 0,
                        uploads_last_60_days: 0,
                        uploads_last_90_days: 0,
                        athlete_count: 0,
                        subscriptions: [],
                        partner: {} as any,
                        creator: {} as any,
                    },
                    {
                        id: 9005,
                        uuid: 'dummy-gold-no-past-due-3',
                        name: 'Gold Elite Training',
                        slug: 'gold-elite-training',
                        about_us: null,
                        logo_url: null,
                        banner_url: null,
                        video_url: null,
                        mux_playback_id: undefined,
                        image_urls: {} as any,
                        object: EntityType.ORGANIZATION,
                        subscription_tier: 'gold',
                        subscription_tier_sort: 3,
                        amount_past_due: 0,
                        total_commissions: 0,
                        marketplace_commissions: 0,
                        pib_bonus_commissions: 0,
                        residual_commissions: 0,
                        check_match_commissions: 0,
                        uploads_last_30_days: 0,
                        uploads_last_60_days: 0,
                        uploads_last_90_days: 0,
                        athlete_count: 0,
                        subscriptions: [],
                        partner: {} as any,
                        creator: {} as any,
                    },
                    // Silver orgs with past due
                    {
                        id: 9006,
                        uuid: 'dummy-silver-past-due-1',
                        name: 'Silver Star Sports',
                        slug: 'silver-star-sports',
                        about_us: null,
                        logo_url: null,
                        banner_url: null,
                        video_url: null,
                        mux_playback_id: undefined,
                        image_urls: {} as any,
                        object: EntityType.ORGANIZATION,
                        subscription_tier: 'silver',
                        subscription_tier_sort: 2,
                        amount_past_due: 150000, // $1,500.00
                        total_commissions: 0,
                        marketplace_commissions: 0,
                        pib_bonus_commissions: 0,
                        residual_commissions: 0,
                        check_match_commissions: 0,
                        uploads_last_30_days: 0,
                        uploads_last_60_days: 0,
                        uploads_last_90_days: 0,
                        athlete_count: 0,
                        subscriptions: [],
                        partner: {} as any,
                        creator: {} as any,
                    },
                    // Silver orgs without past due
                    {
                        id: 9007,
                        uuid: 'dummy-silver-no-past-due-1',
                        name: 'Silver Elite Training',
                        slug: 'silver-elite-training',
                        about_us: null,
                        logo_url: null,
                        banner_url: null,
                        video_url: null,
                        mux_playback_id: undefined,
                        image_urls: {} as any,
                        object: EntityType.ORGANIZATION,
                        subscription_tier: 'silver',
                        subscription_tier_sort: 2,
                        amount_past_due: 0,
                        total_commissions: 0,
                        marketplace_commissions: 0,
                        pib_bonus_commissions: 0,
                        residual_commissions: 0,
                        check_match_commissions: 0,
                        uploads_last_30_days: 0,
                        uploads_last_60_days: 0,
                        uploads_last_90_days: 0,
                        athlete_count: 0,
                        subscriptions: [],
                        partner: {} as any,
                        creator: {} as any,
                    },
                    // Bronze orgs with past due
                    {
                        id: 9008,
                        uuid: 'dummy-bronze-past-due-1',
                        name: 'Bronze Performance Center',
                        slug: 'bronze-performance-center',
                        about_us: null,
                        logo_url: null,
                        banner_url: null,
                        video_url: null,
                        mux_playback_id: undefined,
                        image_urls: {} as any,
                        object: EntityType.ORGANIZATION,
                        subscription_tier: 'bronze',
                        subscription_tier_sort: 1,
                        amount_past_due: 240000, // $2,400.00
                        total_commissions: 0,
                        marketplace_commissions: 0,
                        pib_bonus_commissions: 0,
                        residual_commissions: 0,
                        check_match_commissions: 0,
                        uploads_last_30_days: 0,
                        uploads_last_60_days: 0,
                        uploads_last_90_days: 0,
                        athlete_count: 0,
                        subscriptions: [],
                        partner: {} as any,
                        creator: {} as any,
                    },
                    // Bronze orgs without past due
                    {
                        id: 9009,
                        uuid: 'dummy-bronze-no-past-due-1',
                        name: 'Bronze Athletics Club',
                        slug: 'bronze-athletics-club',
                        about_us: null,
                        logo_url: null,
                        banner_url: null,
                        video_url: null,
                        mux_playback_id: undefined,
                        image_urls: {} as any,
                        object: EntityType.ORGANIZATION,
                        subscription_tier: 'bronze',
                        subscription_tier_sort: 1,
                        amount_past_due: 0,
                        total_commissions: 0,
                        marketplace_commissions: 0,
                        pib_bonus_commissions: 0,
                        residual_commissions: 0,
                        check_match_commissions: 0,
                        uploads_last_30_days: 0,
                        uploads_last_60_days: 0,
                        uploads_last_90_days: 0,
                        athlete_count: 0,
                        subscriptions: [],
                        partner: {} as any,
                        creator: {} as any,
                    },
                    {
                        id: 9010,
                        uuid: 'dummy-bronze-no-past-due-2',
                        name: 'Bronze Training Academy',
                        slug: 'bronze-training-academy',
                        about_us: null,
                        logo_url: null,
                        banner_url: null,
                        video_url: null,
                        mux_playback_id: undefined,
                        image_urls: {} as any,
                        object: EntityType.ORGANIZATION,
                        subscription_tier: 'bronze',
                        subscription_tier_sort: 1,
                        amount_past_due: 0,
                        total_commissions: 0,
                        marketplace_commissions: 0,
                        pib_bonus_commissions: 0,
                        residual_commissions: 0,
                        check_match_commissions: 0,
                        uploads_last_30_days: 0,
                        uploads_last_60_days: 0,
                        uploads_last_90_days: 0,
                        athlete_count: 0,
                        subscriptions: [],
                        partner: {} as any,
                        creator: {} as any,
                    },
                    {
                        id: 9011,
                        uuid: 'dummy-bronze-no-past-due-3',
                        name: 'Bronze Sports Institute',
                        slug: 'bronze-sports-institute',
                        about_us: null,
                        logo_url: null,
                        banner_url: null,
                        video_url: null,
                        mux_playback_id: undefined,
                        image_urls: {} as any,
                        object: EntityType.ORGANIZATION,
                        subscription_tier: 'bronze',
                        subscription_tier_sort: 1,
                        amount_past_due: 0,
                        total_commissions: 0,
                        marketplace_commissions: 0,
                        pib_bonus_commissions: 0,
                        residual_commissions: 0,
                        check_match_commissions: 0,
                        uploads_last_30_days: 0,
                        uploads_last_60_days: 0,
                        uploads_last_90_days: 0,
                        athlete_count: 0,
                        subscriptions: [],
                        partner: {} as any,
                        creator: {} as any,
                    },
                    {
                        id: 9012,
                        uuid: 'dummy-bronze-no-past-due-4',
                        name: 'Bronze Fitness Center',
                        slug: 'bronze-fitness-center',
                        about_us: null,
                        logo_url: null,
                        banner_url: null,
                        video_url: null,
                        mux_playback_id: undefined,
                        image_urls: {} as any,
                        object: EntityType.ORGANIZATION,
                        subscription_tier: 'bronze',
                        subscription_tier_sort: 1,
                        amount_past_due: 0,
                        total_commissions: 0,
                        marketplace_commissions: 0,
                        pib_bonus_commissions: 0,
                        residual_commissions: 0,
                        check_match_commissions: 0,
                        uploads_last_30_days: 0,
                        uploads_last_60_days: 0,
                        uploads_last_90_days: 0,
                        athlete_count: 0,
                        subscriptions: [],
                        partner: {} as any,
                        creator: {} as any,
                    },
                    // Cancelled/Not Subscribed orgs with past due
                    {
                        id: 9013,
                        uuid: 'dummy-cancelled-past-due-1',
                        name: 'Former Elite Sports',
                        slug: 'former-elite-sports',
                        about_us: null,
                        logo_url: null,
                        banner_url: null,
                        video_url: null,
                        mux_playback_id: undefined,
                        image_urls: {} as any,
                        object: EntityType.ORGANIZATION,
                        subscription_tier: undefined, // no tier - should be counted as cancelled
                        subscription_tier_sort: 0,
                        amount_past_due: 1000000, // $10,000.00
                        total_commissions: 0,
                        marketplace_commissions: 0,
                        pib_bonus_commissions: 0,
                        residual_commissions: 0,
                        check_match_commissions: 0,
                        uploads_last_30_days: 0,
                        uploads_last_60_days: 0,
                        uploads_last_90_days: 0,
                        athlete_count: 0,
                        subscriptions: [],
                        partner: {} as any,
                        creator: {} as any,
                    },
                    {
                        id: 9014,
                        uuid: 'dummy-cancelled-past-due-2',
                        name: 'Inactive Training Center',
                        slug: 'inactive-training-center',
                        about_us: null,
                        logo_url: null,
                        banner_url: null,
                        video_url: null,
                        mux_playback_id: undefined,
                        image_urls: {} as any,
                        object: EntityType.ORGANIZATION,
                        subscription_tier: '', // empty string - should be counted as cancelled
                        subscription_tier_sort: 0,
                        amount_past_due: 1000000, // $10,000.00
                        total_commissions: 0,
                        marketplace_commissions: 0,
                        pib_bonus_commissions: 0,
                        residual_commissions: 0,
                        check_match_commissions: 0,
                        uploads_last_30_days: 0,
                        uploads_last_60_days: 0,
                        uploads_last_90_days: 0,
                        athlete_count: 0,
                        subscriptions: [],
                        partner: {} as any,
                        creator: {} as any,
                    },
                    {
                        id: 9015,
                        uuid: 'dummy-cancelled-past-due-3',
                        name: 'Discontinued Athletics',
                        slug: 'discontinued-athletics',
                        about_us: null,
                        logo_url: null,
                        banner_url: null,
                        video_url: null,
                        mux_playback_id: undefined,
                        image_urls: {} as any,
                        object: EntityType.ORGANIZATION,
                        subscription_tier: 'no tier', // "no tier" string - should be counted as cancelled
                        subscription_tier_sort: 0,
                        amount_past_due: 1000000, // $10,000.00
                        total_commissions: 0,
                        marketplace_commissions: 0,
                        pib_bonus_commissions: 0,
                        residual_commissions: 0,
                        check_match_commissions: 0,
                        uploads_last_30_days: 0,
                        uploads_last_60_days: 0,
                        uploads_last_90_days: 0,
                        athlete_count: 0,
                        subscriptions: [],
                        partner: {} as any,
                        creator: {} as any,
                    },
                    // Cancelled/Not Subscribed orgs without past due
                    {
                        id: 9016,
                        uuid: 'dummy-cancelled-no-past-due-1',
                        name: 'Former Sports Club',
                        slug: 'former-sports-club',
                        about_us: null,
                        logo_url: null,
                        banner_url: null,
                        video_url: null,
                        mux_playback_id: undefined,
                        image_urls: {} as any,
                        object: EntityType.ORGANIZATION,
                        subscription_tier: undefined,
                        subscription_tier_sort: 0,
                        amount_past_due: 0,
                        total_commissions: 0,
                        marketplace_commissions: 0,
                        pib_bonus_commissions: 0,
                        residual_commissions: 0,
                        check_match_commissions: 0,
                        uploads_last_30_days: 0,
                        uploads_last_60_days: 0,
                        uploads_last_90_days: 0,
                        athlete_count: 0,
                        subscriptions: [],
                        partner: {} as any,
                        creator: {} as any,
                    },
                    {
                        id: 9017,
                        uuid: 'dummy-cancelled-no-past-due-2',
                        name: 'Inactive Performance',
                        slug: 'inactive-performance',
                        about_us: null,
                        logo_url: null,
                        banner_url: null,
                        video_url: null,
                        mux_playback_id: undefined,
                        image_urls: {} as any,
                        object: EntityType.ORGANIZATION,
                        subscription_tier: '',
                        subscription_tier_sort: 0,
                        amount_past_due: 0,
                        total_commissions: 0,
                        marketplace_commissions: 0,
                        pib_bonus_commissions: 0,
                        residual_commissions: 0,
                        check_match_commissions: 0,
                        uploads_last_30_days: 0,
                        uploads_last_60_days: 0,
                        uploads_last_90_days: 0,
                        athlete_count: 0,
                        subscriptions: [],
                        partner: {} as any,
                        creator: {} as any,
                    },
                    {
                        id: 9018,
                        uuid: 'dummy-cancelled-no-past-due-3',
                        name: 'No Tier Organization',
                        slug: 'no-tier-organization',
                        about_us: null,
                        logo_url: null,
                        banner_url: null,
                        video_url: null,
                        mux_playback_id: undefined,
                        image_urls: {} as any,
                        object: EntityType.ORGANIZATION,
                        subscription_tier: 'no tier',
                        subscription_tier_sort: 0,
                        amount_past_due: 0,
                        total_commissions: 0,
                        marketplace_commissions: 0,
                        pib_bonus_commissions: 0,
                        residual_commissions: 0,
                        check_match_commissions: 0,
                        uploads_last_30_days: 0,
                        uploads_last_60_days: 0,
                        uploads_last_90_days: 0,
                        athlete_count: 0,
                        subscriptions: [],
                        partner: {} as any,
                        creator: {} as any,
                    },
                ];

                // Merge dummy data with real data for local testing
                const allOrgsWithDummy = [...realOrgs, ...dummyOrgs];
                setAllOrganizations(allOrgsWithDummy);
            })
            .catch((error) => {
                console.error('Failed to fetch all organizations for counting', error);
            });
    }, [organization?.data?.uuid]);

    // Fetch paginated organizations for display
    useEffect(() => {
        if (!organization?.data?.uuid) {
            return;
        }

        setIsLoading(true);
        getOrgDashboardMetrics(organization.data.uuid, {
            ...searchParams,
        })
            .then((response) => {
                const orgs = response.data.data;
                // Debug: Log creator data structure
                if (orgs.length > 0 && orgs[0].creator) {
                    console.log('Creator data sample:', orgs[0].creator);
                    console.log('Creator keys:', Object.keys(orgs[0].creator));
                }

                setOrganizations(orgs);
                setTotalOrganizations(response.data.meta.total);
                setHasCompletedInitialLoad(true);
                setIsLoading(false);
            })
            .catch((error) => {
                console.error('Failed to fetch organizations', error);
                setHasCompletedInitialLoad(true);
                setIsLoading(false);
            });
    }, [organization?.data?.uuid, searchParams]);

    const formatCentsToDollars = (cents: number): string => {
        const dollars = cents / 100;
        return `$${dollars.toFixed(2)}`;
    };

    const formatCentsToDollarsNoCents = (cents: number): string => {
        const dollars = cents / 100;
        return `$${Math.round(dollars).toLocaleString('en-US')}`;
    };

    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const columns: GridColDef<AdvancedOrganization, any, any>[] = [
        {
            field: 'name',
            headerName: 'Organization & Contact',
            flex: 1,
            minWidth: 280,
            sortable: true,
            type: 'string',
            sortingOrder: ['asc', 'desc', null],
            renderCell: (params) => {
                const org = params.row;
                const hasAmountDue = (org.amount_past_due || 0) > 0;
                const creator = org.creator as any; // Use any to handle potential field name variations

                // Debug: Log creator structure for troubleshooting
                if (creator && !creator.email && !creator.phone) {
                    console.log('Creator object:', creator);
                    console.log('Creator keys:', Object.keys(creator));
                }

                const contactName = creator
                    ? `${creator.first_name || creator.creator_first_name || ''} ${
                          creator.last_name || creator.creator_last_name || ''
                      }`.trim()
                    : null;
                // Try multiple possible field names for email and phone
                // Backend may return creator_email/creator_phone or email/phone
                const email =
                    creator?.email ||
                    creator?.creator_email ||
                    creator?.email_address ||
                    creator?.user_email;
                const phone =
                    creator?.phone ||
                    creator?.creator_phone ||
                    creator?.phone_number ||
                    creator?.user_phone;

                return (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                            width: '100%',
                            padding: hasAmountDue ? theme.spacing(0.5, 0) : 0,
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: hasAmountDue ? 700 : 600,
                                color: hasAmountDue ? theme.palette.error.main : 'text.primary',
                                fontSize: '0.875rem',
                            }}
                        >
                            {org.name}
                        </Typography>
                        {contactName && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    marginTop: 0.25,
                                }}
                            >
                                <PersonIcon
                                    sx={{
                                        fontSize: '0.75rem',
                                        color: hasAmountDue
                                            ? theme.palette.error.dark
                                            : 'text.secondary',
                                    }}
                                />
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: hasAmountDue
                                            ? theme.palette.error.dark
                                            : 'text.secondary',
                                        fontSize: '0.75rem',
                                        fontWeight: hasAmountDue ? 500 : 400,
                                    }}
                                >
                                    {contactName}
                                </Typography>
                            </Box>
                        )}
                        {email && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                }}
                            >
                                <EmailIcon
                                    sx={{
                                        fontSize: '0.75rem',
                                        color: hasAmountDue
                                            ? theme.palette.error.dark
                                            : 'text.secondary',
                                    }}
                                />
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: hasAmountDue
                                            ? theme.palette.error.dark
                                            : 'text.secondary',
                                        fontSize: '0.75rem',
                                        wordBreak: 'break-word',
                                        fontWeight: hasAmountDue ? 500 : 400,
                                    }}
                                >
                                    {email}
                                </Typography>
                            </Box>
                        )}
                        {phone && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                }}
                            >
                                <PhoneIcon
                                    sx={{
                                        fontSize: '0.75rem',
                                        color: hasAmountDue
                                            ? theme.palette.error.dark
                                            : 'text.secondary',
                                    }}
                                />
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: hasAmountDue
                                            ? theme.palette.error.dark
                                            : 'text.secondary',
                                        fontSize: '0.75rem',
                                        fontWeight: hasAmountDue ? 500 : 400,
                                    }}
                                >
                                    {phone}
                                </Typography>
                            </Box>
                        )}
                        {!contactName && !email && !phone && (
                            <Typography
                                variant="caption"
                                sx={{
                                    color: 'text.disabled',
                                    fontSize: '0.75rem',
                                    fontStyle: 'italic',
                                }}
                            >
                                No contact information available
                            </Typography>
                        )}
                    </Box>
                );
            },
        },
        {
            field: 'sponsor_name',
            headerName: 'Sponsor',
            flex: 1,
            minWidth: 150,
            sortable: false,
            type: 'string',
        },
        {
            field: 'subscription_level',
            headerName: 'Subscription Level',
            flex: 1,
            minWidth: 140,
            sortable: true,
            type: 'string',
            renderCell: (params) => {
                return params.row.subscription_tier;
            },
            cellClassName: (params) => {
                const tier = params.row.subscription_tier?.toLowerCase() || '';
                if (tier === 'gold') return 'subscription-tier-gold';
                if (tier === 'silver') return 'subscription-tier-silver';
                if (tier === 'bronze') return 'subscription-tier-bronze';
                return '';
            },
        },
        {
            field: 'amount_past_due',
            headerName: 'Amount Due',
            flex: 1,
            minWidth: 180,
            sortable: true,
            type: 'number',
            renderCell: (params) => {
                const amountDue = params.row.amount_past_due || 0;
                const hasBalance = amountDue > 0;
                const formattedAmount = formatCentsToDollars(amountDue);

                if (hasBalance) {
                    return (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                width: '100%',
                                justifyContent: 'flex-start',
                            }}
                        >
                            <Chip
                                icon={<WarningIcon sx={{ color: theme.palette.error.main }} />}
                                label={formattedAmount}
                                sx={{
                                    backgroundColor: `${theme.palette.error.main}15`,
                                    color: theme.palette.error.main,
                                    fontWeight: 'bold',
                                    fontSize: '0.875rem',
                                    height: '32px',
                                    border: `1px solid ${theme.palette.error.main}`,
                                    '& .MuiChip-icon': {
                                        color: theme.palette.error.main,
                                    },
                                    boxShadow: `0 2px 8px ${theme.palette.error.main}40`,
                                    animation: hasBalance
                                        ? 'pulse 2s ease-in-out infinite'
                                        : 'none',
                                    '@keyframes pulse': {
                                        '0%, 100%': {
                                            opacity: 1,
                                        },
                                        '50%': {
                                            opacity: 0.8,
                                        },
                                    },
                                }}
                            />
                        </Box>
                    );
                }

                return (
                    <Typography
                        sx={{
                            color: 'text.secondary',
                            fontSize: '0.875rem',
                        }}
                    >
                        {formattedAmount}
                    </Typography>
                );
            },
            cellClassName: (params) => {
                return (params.row.amount_past_due || 0) > 0 ? 'amount-due-cell' : '';
            },
        },
        {
            field: 'uploads_last_30_days',
            headerName: '30d',
            flex: 1,
            minWidth: 130,
            sortable: true,
            sortingOrder: ['desc', 'asc', null],
            type: 'number',
            description: 'Uploads in the last 30 calendar days including today',
            renderCell: (params) => {
                const last30 = params.row.uploads_last_30_days || 0;
                const last60 = params.row.uploads_last_60_days || 0;
                const prior30 = Math.max(0, last60 - last30);
                let icon = <TrendingFlatIcon fontSize="small" sx={{ color: 'text.secondary' }} />;
                if (last30 > prior30) {
                    icon = <TrendingUpIcon fontSize="small" sx={{ color: 'success.main' }} />;
                } else if (last30 < prior30) {
                    icon = <TrendingDownIcon fontSize="small" sx={{ color: 'error.main' }} />;
                }
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2">{last30}</Typography>
                        {icon}
                    </Box>
                );
            },
        },
        {
            field: 'uploads_last_60_days',
            headerName: '60d',
            flex: 1,
            minWidth: 130,
            sortable: true,
            sortingOrder: ['desc', 'asc', null],
            type: 'number',
            description: 'Uploads in the last 60 calendar days including today',
        },
        {
            field: 'uploads_last_90_days',
            headerName: '90d',
            flex: 1,
            minWidth: 130,
            sortable: true,
            sortingOrder: ['desc', 'asc', null],
            type: 'number',
            description: 'Uploads in the last 90 calendar days including today',
        },
        {
            field: 'total_commissions',
            headerName: 'Total',
            flex: 1,
            minWidth: 160,
            sortable: true,
            type: 'number',
            renderCell: (params) => formatCentsToDollars(params.row.total_commissions || 0),
            headerAlign: 'right',
            align: 'right',
        },
        {
            field: 'marketplace_commissions',
            headerName: 'Marketplace',
            flex: 1,
            minWidth: 140,
            sortable: true,
            type: 'number',
            renderCell: (params) => formatCentsToDollars(params.row.marketplace_commissions || 0),
            headerAlign: 'right',
            align: 'right',
        },
        {
            field: 'pib_bonus_commissions',
            headerName: 'PIB Bonus',
            flex: 1,
            minWidth: 130,
            sortable: true,
            type: 'number',
            renderCell: (params) => formatCentsToDollars(params.row.pib_bonus_commissions || 0),
            headerAlign: 'right',
            align: 'right',
        },
        {
            field: 'residual_commissions',
            headerName: 'Recurring',
            flex: 1,
            minWidth: 130,
            sortable: true,
            type: 'number',
            renderCell: (params) => formatCentsToDollars(params.row.residual_commissions || 0),
            headerAlign: 'right',
            align: 'right',
        },
        {
            field: 'check_match_commissions',
            headerName: 'Check Match',
            flex: 1,
            minWidth: 140,
            sortable: true,
            type: 'number',
            renderCell: (params) => formatCentsToDollars(params.row.check_match_commissions || 0),
            headerAlign: 'right',
            align: 'right',
        },
        {
            field: 'pods_org',
            headerName: 'Org Pods',
            flex: 1,
            minWidth: 120,
            sortable: true,
            type: 'number',
            headerAlign: 'right',
            align: 'right',
        },
        {
            field: 'org_pod_subscriptions',
            headerName: 'Org Pod Subscriptions',
            flex: 1,
            minWidth: 140,
            sortable: true,
            type: 'number',
            headerAlign: 'right',
            align: 'right',
        },
        {
            field: 'pods_athlete',
            headerName: 'Athlete Pods',
            flex: 1,
            minWidth: 130,
            sortable: true,
            type: 'number',
            headerAlign: 'right',
            align: 'right',
        },
        {
            field: 'athlete_pod_subscriptions',
            headerName: 'Athlete Pod Subscriptions',
            flex: 1,
            minWidth: 150,
            sortable: true,
            type: 'number',
            headerAlign: 'right',
            align: 'right',
        },
        {
            field: 'athlete_count',
            headerName: 'Athlete Count',
            flex: 1,
            minWidth: 130,
            sortable: true,
            type: 'number',
        },
        {
            field: 'created_at',
            headerName: 'Joined',
            flex: 1,
            minWidth: 120,
            sortable: true,
            type: 'string',
            renderCell: (params) => formatDate(params.value),
        },
    ];

    // Auto-filter when organization selection changes
    useEffect(() => {
        setSearchParams((prevParams) => {
            const newParams = { ...prevParams, page: 1 };

            if (selectedOrg) {
                newParams.organization_uuid = selectedOrg.uuid;
            } else {
                delete newParams.organization_uuid;
            }

            return newParams;
        });
    }, [selectedOrg]);

    const fetchOrganizations = async (inputValue: string) => {
        try {
            const response = await getTierOneOrgs(organization?.data?.uuid as string, {
                name: inputValue,
            });
            setOrgOptions(response.data.data);
        } catch (error) {
            console.error('Failed to fetch organizations', error);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedFetchOrganizations = useCallback(
        debounce((inputValue: string) => {
            fetchOrganizations(inputValue).then(() => {});
        }, 300),
        [organization?.data?.uuid],
    );

    /**
     * Server-side sort: must update searchParams.sort so pagination, filters, and the
     * paginated useEffect stay in sync. Previously we only fetched here without updating
     * searchParams, so changing page re-fetched with the old sort and sorting looked broken.
     */
    const handleOrgSort = (model: GridSortModel) => {
        if (!model.length || !model[0].field || model[0].sort == null) {
            setSortModel([{ field: 'uploads_last_30_days', sort: 'desc' }]);
            setSearchParams((prev) => ({
                ...prev,
                sort: '-uploads_last_30_days',
                page: 1,
            }));
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
            return;
        }

        setSortModel(model);

        let sortField = model[0].field;
        const sortOrder = model[0].sort;

        if (sortField === 'subscription_level') {
            sortField = 'subscription_tier_sort';
        }

        if (sortOrder === 'desc') {
            sortField = `-${sortField}`;
        }

        setSearchParams((prev) => ({
            ...prev,
            sort: sortField as GetDashboardOrganizationParams['sort'],
            page: 1,
        }));
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
    };

    const handleRowClick = (params: any) => {
        if (!params.row.uuid) {
            return;
        }
        // Find the organization from the organizations array
        const clickedOrg = organizations.find((org) => org.uuid === params.row.uuid);
        setSelectedOrganization(clickedOrg || null);
        // Open modal
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedOrganization(null);
    };

    const handleFaceTimeClick = (phone: string) => {
        if (phone) {
            window.location.href = `facetime://${phone}`;
        }
    };

    const handleIMessageClick = (phone: string) => {
        if (phone) {
            window.location.href = `sms://${phone}`;
        }
    };

    // Use allOrganizations for accurate counts regardless of pagination
    const orgsWithAmountDue = allOrganizations.filter(
        (org) => (org.amount_past_due || 0) > 0,
    ).length;

    // Calculate gold orgs with past due
    const goldOrgsWithPastDue = allOrganizations.filter(
        (org) =>
            (org.subscription_tier?.toLowerCase() === 'gold' || false) &&
            (org.amount_past_due || 0) > 0,
    );
    const goldOrgsCount = goldOrgsWithPastDue.length;
    const totalGoldPastDue = goldOrgsWithPastDue.reduce(
        (sum, org) => sum + (org.amount_past_due || 0),
        0,
    );

    // Calculate silver orgs with past due
    const silverOrgsWithPastDue = allOrganizations.filter(
        (org) =>
            (org.subscription_tier?.toLowerCase() === 'silver' || false) &&
            (org.amount_past_due || 0) > 0,
    );
    const silverOrgsCount = silverOrgsWithPastDue.length;
    const totalSilverPastDue = silverOrgsWithPastDue.reduce(
        (sum, org) => sum + (org.amount_past_due || 0),
        0,
    );

    // Calculate bronze orgs with past due
    const bronzeOrgsWithPastDue = allOrganizations.filter(
        (org) =>
            (org.subscription_tier?.toLowerCase() === 'bronze' || false) &&
            (org.amount_past_due || 0) > 0,
    );
    const bronzeOrgsCount = bronzeOrgsWithPastDue.length;
    const totalBronzePastDue = bronzeOrgsWithPastDue.reduce(
        (sum, org) => sum + (org.amount_past_due || 0),
        0,
    );

    // Calculate cancelled subscription orgs with past due
    // Orgs without a subscription tier (null, undefined, empty string, "no tier") are considered cancelled
    const cancelledOrgsWithPastDue = allOrganizations.filter((org) => {
        const tier = org.subscription_tier;
        const tierSort = org.subscription_tier_sort;
        // Check for null, undefined, empty string, whitespace-only, string "null", or "no tier"
        // Also check if subscription_tier_sort is 0 (which corresponds to organizationSubscriptionLevel: 0)
        const hasNoTier =
            tier == null ||
            tier === '' ||
            (typeof tier === 'string' && tier.trim() === '') ||
            (typeof tier === 'string' && tier.toLowerCase() === 'null') ||
            (typeof tier === 'string' && tier.toLowerCase() === 'no tier') ||
            tierSort === 0;
        const isExplicitlyCancelled =
            typeof tier === 'string' && tier.toLowerCase() === 'cancelled';
        return (hasNoTier || isExplicitlyCancelled) && (org.amount_past_due || 0) > 0;
    });
    const cancelledOrgsCount = cancelledOrgsWithPastDue.length;
    const totalCancelledPastDue = cancelledOrgsWithPastDue.reduce(
        (sum, org) => sum + (org.amount_past_due || 0),
        0,
    );

    // Calculate total counts by subscription tier (not just with past due)
    // Use allOrganizations to get accurate counts regardless of pagination
    const totalGoldOrgs = allOrganizations.filter(
        (org) => org.subscription_tier?.toLowerCase() === 'gold',
    ).length;
    const totalSilverOrgs = allOrganizations.filter(
        (org) => org.subscription_tier?.toLowerCase() === 'silver',
    ).length;
    const totalBronzeOrgs = allOrganizations.filter(
        (org) => org.subscription_tier?.toLowerCase() === 'bronze',
    ).length;
    const totalCancelledOrgs = allOrganizations.filter((org) => {
        const tier = org.subscription_tier;
        const tierSort = org.subscription_tier_sort;
        // Check for null, undefined, empty string, whitespace-only, string "null", or "no tier"
        // Also check if subscription_tier_sort is 0 (which corresponds to organizationSubscriptionLevel: 0)
        const hasNoTier =
            tier == null ||
            tier === '' ||
            (typeof tier === 'string' && tier.trim() === '') ||
            (typeof tier === 'string' && tier.toLowerCase() === 'null') ||
            (typeof tier === 'string' && tier.toLowerCase() === 'no tier') ||
            tierSort === 0;
        const isExplicitlyCancelled =
            typeof tier === 'string' && tier.toLowerCase() === 'cancelled';
        return hasNoTier || isExplicitlyCancelled;
    }).length;

    // Filter organizations based on active filter
    const filteredOrganizations = React.useMemo(() => {
        if (!activeFilter) {
            return organizations;
        }

        switch (activeFilter) {
            case 'gold-past-due':
                return organizations.filter(
                    (org) =>
                        (org.subscription_tier?.toLowerCase() === 'gold' || false) &&
                        (org.amount_past_due || 0) > 0,
                );
            case 'silver-past-due':
                return organizations.filter(
                    (org) =>
                        (org.subscription_tier?.toLowerCase() === 'silver' || false) &&
                        (org.amount_past_due || 0) > 0,
                );
            case 'bronze-past-due':
                return organizations.filter(
                    (org) =>
                        (org.subscription_tier?.toLowerCase() === 'bronze' || false) &&
                        (org.amount_past_due || 0) > 0,
                );
            case 'cancelled-past-due':
                return organizations.filter((org) => {
                    const tier = org.subscription_tier;
                    const tierSort = org.subscription_tier_sort;
                    const hasNoTier =
                        tier == null ||
                        tier === '' ||
                        (typeof tier === 'string' && tier.trim() === '') ||
                        (typeof tier === 'string' && tier.toLowerCase() === 'null') ||
                        (typeof tier === 'string' && tier.toLowerCase() === 'no tier') ||
                        tierSort === 0;
                    const isExplicitlyCancelled =
                        typeof tier === 'string' && tier.toLowerCase() === 'cancelled';
                    return (hasNoTier || isExplicitlyCancelled) && (org.amount_past_due || 0) > 0;
                });
            case 'all-past-due':
                return organizations.filter((org) => (org.amount_past_due || 0) > 0);
            default:
                return organizations;
        }
    }, [organizations, activeFilter]);

    // Handler to clear filter
    const handleClearFilter = () => {
        setActiveFilter(null);
    };

    return (
        <Box
            sx={{
                position: 'relative',
                padding: theme.spacing(3),
                minHeight: '100vh',
            }}
        >
            {isLoading && (
                <Box
                    sx={{
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 9999,
                        backgroundColor: 'white',
                        borderRadius: 2,
                        padding: theme.spacing(3),
                        boxShadow: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2,
                    }}
                >
                    <CircularProgress size={60} />
                    <Typography variant="body2" color="text.secondary">
                        Loading...
                    </Typography>
                </Box>
            )}
            <Box sx={{ maxWidth: '100%', margin: '0 auto' }}>
                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: 600,
                        marginBottom: theme.spacing(1),
                        color: 'text.primary',
                    }}
                >
                    Organization Metrics
                </Typography>
                {/* Total Subscription Tier Count Cards */}
                <Grid container spacing={3} sx={{ marginBottom: theme.spacing(4) }}>
                    {/* Total Gold Orgs Card */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper
                            elevation={4}
                            sx={{
                                padding: theme.spacing(3),
                                background: `linear-gradient(135deg, #FFD700 0%, #FFA500 100%)`,
                                border: `3px solid #FFD700`,
                                borderRadius: 4,
                                boxShadow: `0 8px 24px rgba(255, 215, 0, 0.4)`,
                                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: `0 12px 32px rgba(255, 215, 0, 0.5)`,
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 2,
                                }}
                            >
                                <Typography
                                    variant="h5"
                                    sx={{
                                        fontWeight: 700,
                                        color: '#000',
                                        fontSize: '1.1rem',
                                        textAlign: 'center',
                                    }}
                                >
                                    Gold Organizations
                                </Typography>
                                <Typography
                                    variant="h2"
                                    sx={{
                                        fontWeight: 900,
                                        color: '#000',
                                        fontSize: '3.5rem',
                                        lineHeight: 1,
                                    }}
                                >
                                    {totalGoldOrgs}
                                </Typography>
                            </Box>
                        </Paper>
                    </Grid>
                    {/* Total Silver Orgs Card */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper
                            elevation={4}
                            sx={{
                                padding: theme.spacing(3),
                                background: `linear-gradient(135deg, #C0C0C0 0%, #A8A8A8 100%)`,
                                border: `3px solid #C0C0C0`,
                                borderRadius: 4,
                                boxShadow: `0 8px 24px rgba(192, 192, 192, 0.4)`,
                                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: `0 12px 32px rgba(192, 192, 192, 0.5)`,
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 2,
                                }}
                            >
                                <Typography
                                    variant="h5"
                                    sx={{
                                        fontWeight: 700,
                                        color: '#000',
                                        fontSize: '1.1rem',
                                        textAlign: 'center',
                                    }}
                                >
                                    Silver Organizations
                                </Typography>
                                <Typography
                                    variant="h2"
                                    sx={{
                                        fontWeight: 900,
                                        color: '#000',
                                        fontSize: '3.5rem',
                                        lineHeight: 1,
                                    }}
                                >
                                    {totalSilverOrgs}
                                </Typography>
                            </Box>
                        </Paper>
                    </Grid>
                    {/* Total Bronze Orgs Card */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper
                            elevation={4}
                            sx={{
                                padding: theme.spacing(3),
                                background: `linear-gradient(135deg, #CD7F32 0%, #B87333 100%)`,
                                border: `3px solid #CD7F32`,
                                borderRadius: 4,
                                boxShadow: `0 8px 24px rgba(205, 127, 50, 0.4)`,
                                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: `0 12px 32px rgba(205, 127, 50, 0.5)`,
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 2,
                                }}
                            >
                                <Typography
                                    variant="h5"
                                    sx={{
                                        fontWeight: 700,
                                        color: '#000',
                                        fontSize: '1.1rem',
                                        textAlign: 'center',
                                    }}
                                >
                                    Bronze Organizations
                                </Typography>
                                <Typography
                                    variant="h2"
                                    sx={{
                                        fontWeight: 900,
                                        color: '#000',
                                        fontSize: '3.5rem',
                                        lineHeight: 1,
                                    }}
                                >
                                    {totalBronzeOrgs}
                                </Typography>
                            </Box>
                        </Paper>
                    </Grid>
                    {/* Total Cancelled Orgs Card */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper
                            elevation={4}
                            sx={{
                                padding: theme.spacing(3),
                                background: `linear-gradient(135deg, #DC143C 0%, #8B0000 100%)`,
                                border: `3px solid #DC143C`,
                                borderRadius: 4,
                                boxShadow: `0 8px 24px rgba(220, 20, 60, 0.4)`,
                                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: `0 12px 32px rgba(220, 20, 60, 0.5)`,
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 2,
                                }}
                            >
                                <Typography
                                    variant="h5"
                                    sx={{
                                        fontWeight: 700,
                                        color: '#FFF',
                                        fontSize: '1.1rem',
                                        textAlign: 'center',
                                    }}
                                >
                                    Organizations Not Subscribed
                                </Typography>
                                <Typography
                                    variant="h2"
                                    sx={{
                                        fontWeight: 900,
                                        color: '#FFF',
                                        fontSize: '3.5rem',
                                        lineHeight: 1,
                                    }}
                                >
                                    {totalCancelledOrgs}
                                </Typography>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
                {/* Past Due Summary Cards - Alert Style */}
                {(goldOrgsCount > 0 ||
                    silverOrgsCount > 0 ||
                    bronzeOrgsCount > 0 ||
                    cancelledOrgsCount > 0 ||
                    orgsWithAmountDue > 0) && (
                    <Box sx={{ marginBottom: theme.spacing(3) }}>
                        <Grid container spacing={2}>
                            {/* Gold Orgs with Past Due Card */}
                            {goldOrgsCount > 0 && (
                                <Grid item xs={12} sm={6} md={4}>
                                    <Paper
                                        elevation={4}
                                        onClick={() => setActiveFilter('gold-past-due')}
                                        sx={{
                                            padding: theme.spacing(2.5),
                                            background: theme.palette.background.paper,
                                            border: `3px solid ${theme.palette.warning.main}`,
                                            borderRadius: 3,
                                            boxShadow: `0 4px 16px ${theme.palette.warning.main}40`,
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: `0 6px 20px ${theme.palette.warning.main}60`,
                                            },
                                            ...(activeFilter === 'gold-past-due' && {
                                                border: `3px solid ${theme.palette.primary.main}`,
                                                boxShadow: `0 6px 20px ${theme.palette.primary.main}60`,
                                            }),
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: 2,
                                            }}
                                        >
                                            <WarningIcon
                                                sx={{
                                                    fontSize: '2.5rem',
                                                    color: theme.palette.warning.main,
                                                    mt: 0.5,
                                                }}
                                            />
                                            <Box sx={{ flex: 1 }}>
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        fontWeight: 700,
                                                        color: theme.palette.error.main,
                                                        fontSize: '1.1rem',
                                                        mb: 1,
                                                    }}
                                                >
                                                    {goldOrgsCount} Gold Organizations with Past
                                                    Due:{' '}
                                                    {formatCentsToDollarsNoCents(totalGoldPastDue)}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Paper>
                                </Grid>
                            )}
                            {/* Silver Orgs with Past Due Card */}
                            {silverOrgsCount > 0 && (
                                <Grid item xs={12} sm={6} md={4}>
                                    <Paper
                                        elevation={4}
                                        onClick={() => setActiveFilter('silver-past-due')}
                                        sx={{
                                            padding: theme.spacing(2.5),
                                            background: theme.palette.background.paper,
                                            border: `3px solid ${theme.palette.warning.main}`,
                                            borderRadius: 3,
                                            boxShadow: `0 4px 16px ${theme.palette.warning.main}40`,
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: `0 6px 20px ${theme.palette.warning.main}60`,
                                            },
                                            ...(activeFilter === 'silver-past-due' && {
                                                border: `3px solid ${theme.palette.primary.main}`,
                                                boxShadow: `0 6px 20px ${theme.palette.primary.main}60`,
                                            }),
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: 2,
                                            }}
                                        >
                                            <WarningIcon
                                                sx={{
                                                    fontSize: '2.5rem',
                                                    color: theme.palette.warning.main,
                                                    mt: 0.5,
                                                }}
                                            />
                                            <Box sx={{ flex: 1 }}>
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        fontWeight: 700,
                                                        color: theme.palette.error.main,
                                                        fontSize: '1.1rem',
                                                    }}
                                                >
                                                    {silverOrgsCount} Silver Organizations with Past
                                                    Due:{' '}
                                                    {formatCentsToDollarsNoCents(
                                                        totalSilverPastDue,
                                                    )}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Paper>
                                </Grid>
                            )}
                            {/* Bronze Orgs with Past Due Card */}
                            {bronzeOrgsCount > 0 && (
                                <Grid item xs={12} sm={6} md={4}>
                                    <Paper
                                        elevation={4}
                                        onClick={() => setActiveFilter('bronze-past-due')}
                                        sx={{
                                            padding: theme.spacing(2.5),
                                            background: theme.palette.background.paper,
                                            border: `3px solid ${theme.palette.warning.main}`,
                                            borderRadius: 3,
                                            boxShadow: `0 4px 16px ${theme.palette.warning.main}40`,
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: `0 6px 20px ${theme.palette.warning.main}60`,
                                            },
                                            ...(activeFilter === 'bronze-past-due' && {
                                                border: `3px solid ${theme.palette.primary.main}`,
                                                boxShadow: `0 6px 20px ${theme.palette.primary.main}60`,
                                            }),
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: 2,
                                            }}
                                        >
                                            <WarningIcon
                                                sx={{
                                                    fontSize: '2.5rem',
                                                    color: theme.palette.warning.main,
                                                    mt: 0.5,
                                                }}
                                            />
                                            <Box sx={{ flex: 1 }}>
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        fontWeight: 700,
                                                        color: theme.palette.error.main,
                                                        fontSize: '1.1rem',
                                                    }}
                                                >
                                                    {bronzeOrgsCount} Bronze Organizations with Past
                                                    Due:{' '}
                                                    {formatCentsToDollarsNoCents(
                                                        totalBronzePastDue,
                                                    )}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Paper>
                                </Grid>
                            )}
                            {/* Cancelled Subscription Orgs with Past Due Card */}
                            {cancelledOrgsCount > 0 && (
                                <Grid item xs={12} sm={6} md={4}>
                                    <Paper
                                        elevation={4}
                                        onClick={() => setActiveFilter('cancelled-past-due')}
                                        sx={{
                                            padding: theme.spacing(2.5),
                                            background: theme.palette.background.paper,
                                            border: `3px solid ${theme.palette.error.main}`,
                                            borderRadius: 3,
                                            boxShadow: `0 4px 16px ${theme.palette.error.main}40`,
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: `0 6px 20px ${theme.palette.error.main}60`,
                                            },
                                            ...(activeFilter === 'cancelled-past-due' && {
                                                border: `3px solid ${theme.palette.primary.main}`,
                                                boxShadow: `0 6px 20px ${theme.palette.primary.main}60`,
                                            }),
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: 2,
                                            }}
                                        >
                                            <WarningIcon
                                                sx={{
                                                    fontSize: '2.5rem',
                                                    color: theme.palette.error.main,
                                                    mt: 0.5,
                                                }}
                                            />
                                            <Box sx={{ flex: 1 }}>
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        fontWeight: 700,
                                                        color: theme.palette.error.main,
                                                        fontSize: '1.1rem',
                                                    }}
                                                >
                                                    {cancelledOrgsCount} Organizations Not
                                                    Subscribed with Past Due:{' '}
                                                    {formatCentsToDollarsNoCents(
                                                        totalCancelledPastDue,
                                                    )}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Paper>
                                </Grid>
                            )}
                            {/* Organizations with Amount Due Card */}
                            {orgsWithAmountDue > 0 && (
                                <Grid item xs={12} sm={6} md={4}>
                                    <Paper
                                        elevation={4}
                                        onClick={() => setActiveFilter('all-past-due')}
                                        sx={{
                                            padding: theme.spacing(2.5),
                                            background: theme.palette.background.paper,
                                            border: `3px solid ${theme.palette.error.main}`,
                                            borderRadius: 3,
                                            boxShadow: `0 4px 16px ${theme.palette.error.main}40`,
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: `0 6px 20px ${theme.palette.error.main}60`,
                                            },
                                            ...(activeFilter === 'all-past-due' && {
                                                border: `3px solid ${theme.palette.primary.main}`,
                                                boxShadow: `0 6px 20px ${theme.palette.primary.main}60`,
                                            }),
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: 2,
                                            }}
                                        >
                                            <WarningIcon
                                                sx={{
                                                    fontSize: '2.5rem',
                                                    color: theme.palette.error.main,
                                                    mt: 0.5,
                                                }}
                                            />
                                            <Box sx={{ flex: 1 }}>
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        fontWeight: 700,
                                                        color: theme.palette.error.main,
                                                        fontSize: '1.1rem',
                                                    }}
                                                >
                                                    Organizations with Amount Due:{' '}
                                                    {orgsWithAmountDue}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Paper>
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                )}
                <Box
                    sx={{
                        marginBottom: theme.spacing(3),
                    }}
                >
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'text.secondary',
                            mb: 2,
                        }}
                    >
                        Your organization and all level 1 tier partners
                    </Typography>
                </Box>
                <Box
                    display="flex"
                    alignItems="center"
                    sx={{
                        marginBottom: theme.spacing(3),
                        flexWrap: 'wrap',
                        gap: 2,
                    }}
                >
                    {activeFilter && (
                        <Chip
                            label={`Filter: ${activeFilter
                                .replace(/-/g, ' ')
                                .replace(/\b\w/g, (l) => l.toUpperCase())}`}
                            onDelete={handleClearFilter}
                            color="primary"
                        />
                    )}
                    <Autocomplete
                        size="medium"
                        options={orgOptions}
                        getOptionLabel={(option) => option.name}
                        onInputChange={(event, value) => debouncedFetchOrganizations(value)}
                        onChange={(event, value) => setSelectedOrg(value)}
                        value={selectedOrg}
                        clearOnBlur={false}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Filter by Organization"
                                placeholder="Search organizations..."
                            />
                        )}
                        sx={{ minWidth: 300, flex: 1, maxWidth: 400 }}
                    />
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        marginBottom: theme.spacing(2),
                        padding: theme.spacing(1.5, 2),
                        backgroundColor: `${theme.palette.info.light}15`,
                        borderRadius: 1,
                        border: `1px solid ${theme.palette.info.main}40`,
                    }}
                >
                    <InfoIcon
                        sx={{
                            fontSize: '1.2rem',
                            color: theme.palette.info.main,
                        }}
                    />
                    <Typography
                        variant="body2"
                        sx={{
                            color: theme.palette.info.dark,
                            fontSize: '0.875rem',
                        }}
                    >
                        <strong>Tip:</strong> Click on any organization name in the
                        &quot;Organization & Contact&quot; column to view detailed contact
                        information.
                    </Typography>
                </Box>
                <Paper
                    elevation={2}
                    sx={{
                        width: '100%',
                        height: 'calc(100vh - 280px)',
                        minHeight: 600,
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    {organizations.length === 0 ? (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%',
                                padding: theme.spacing(4),
                            }}
                        >
                            {isLoading || !hasCompletedInitialLoad ? (
                                <>
                                    <CircularProgress size={40} />
                                    <Typography variant="h6" color="text.secondary" gutterBottom>
                                        Loading organizations...
                                    </Typography>
                                </>
                            ) : (
                                <>
                                    <Typography variant="h6" color="text.secondary" gutterBottom>
                                        No organizations found
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {selectedOrg
                                            ? 'Try selecting a different organization or clear the filter.'
                                            : 'No data available at this time.'}
                                    </Typography>
                                </>
                            )}
                        </Box>
                    ) : (
                        <DataGridPremium
                            rows={filteredOrganizations}
                            columns={columns}
                            columnGroupingModel={ORG_METRICS_COLUMN_GROUPING_MODEL}
                            checkboxSelection={false}
                            sortingMode="server"
                            sortingOrder={['desc', 'asc', null]}
                            onSortModelChange={(model: GridSortModel) => {
                                handleOrgSort(model);
                            }}
                            sortModel={sortModel}
                            getRowId={(row) => row.id}
                            onRowClick={handleRowClick}
                            getRowClassName={(params) => {
                                const subscriptionTier = params.row.subscription_tier;
                                const tierSort = params.row.subscription_tier_sort;
                                const tier =
                                    typeof subscriptionTier === 'string'
                                        ? subscriptionTier.toLowerCase()
                                        : '';
                                const isGold = tier === 'gold';
                                const isSilver = tier === 'silver';
                                const isBronze = tier === 'bronze';
                                // Check for null, undefined, empty string, whitespace-only, string "null", or "no tier"
                                // Also check if subscription_tier_sort is 0 (which corresponds to organizationSubscriptionLevel: 0)
                                const isCancelled =
                                    subscriptionTier == null ||
                                    subscriptionTier === '' ||
                                    (typeof subscriptionTier === 'string' &&
                                        subscriptionTier.trim() === '') ||
                                    (typeof subscriptionTier === 'string' &&
                                        subscriptionTier.toLowerCase() === 'null') ||
                                    (typeof subscriptionTier === 'string' &&
                                        subscriptionTier.toLowerCase() === 'no tier') ||
                                    tierSort === 0 ||
                                    tier === 'cancelled';
                                const hasPastDue = (params.row.amount_past_due || 0) > 0;

                                if (isGold && hasPastDue) {
                                    return 'gold-past-due-row';
                                }
                                if (isSilver && hasPastDue) {
                                    return 'silver-past-due-row';
                                }
                                if (isBronze && hasPastDue) {
                                    return 'bronze-past-due-row';
                                }
                                if (isCancelled && hasPastDue) {
                                    return 'cancelled-past-due-row';
                                }
                                if (hasPastDue) {
                                    return 'amount-due-row';
                                }
                                return '';
                            }}
                            initialState={{
                                pinnedColumns: { left: ['name'] },
                            }}
                            pagination
                            pageSizeOptions={[5, 10, 20, 50, 100]}
                            paginationModel={paginationModel}
                            onPaginationModelChange={handlePaginationModelChange}
                            rowCount={totalOrganizations}
                            paginationMode="server"
                            disableColumnMenu={false}
                            showCellVerticalBorder={true}
                            sx={{
                                '& .MuiDataGrid-cell:focus': {
                                    outline: 'none',
                                },
                                '& .MuiDataGrid-cell:focus-within': {
                                    outline: 'none',
                                },
                                '& .MuiDataGrid-row': {
                                    cursor: 'pointer',
                                    '&:hover': {
                                        backgroundColor: theme.palette.action.hover,
                                    },
                                    '&.gold-past-due-row': {
                                        background: `linear-gradient(135deg, rgba(255, 215, 0, 0.15) 0%, rgba(255, 165, 0, 0.15) 100%)`,
                                        borderLeft: `4px solid #FFD700`,
                                        borderTop: `1px solid rgba(255, 215, 0, 0.3)`,
                                        borderBottom: `1px solid rgba(255, 215, 0, 0.3)`,
                                        '&:hover': {
                                            background: `linear-gradient(135deg, rgba(255, 215, 0, 0.25) 0%, rgba(255, 165, 0, 0.25) 100%)`,
                                        },
                                    },
                                    '&.silver-past-due-row': {
                                        background: `linear-gradient(135deg, rgba(192, 192, 192, 0.15) 0%, rgba(168, 168, 168, 0.15) 100%)`,
                                        borderLeft: `4px solid #C0C0C0`,
                                        borderTop: `1px solid rgba(192, 192, 192, 0.3)`,
                                        borderBottom: `1px solid rgba(192, 192, 192, 0.3)`,
                                        '&:hover': {
                                            background: `linear-gradient(135deg, rgba(192, 192, 192, 0.25) 0%, rgba(168, 168, 168, 0.25) 100%)`,
                                        },
                                    },
                                    '&.bronze-past-due-row': {
                                        background: `linear-gradient(135deg, rgba(205, 127, 50, 0.15) 0%, rgba(184, 115, 51, 0.15) 100%)`,
                                        borderLeft: `4px solid #CD7F32`,
                                        borderTop: `1px solid rgba(205, 127, 50, 0.3)`,
                                        borderBottom: `1px solid rgba(205, 127, 50, 0.3)`,
                                        '&:hover': {
                                            background: `linear-gradient(135deg, rgba(205, 127, 50, 0.25) 0%, rgba(184, 115, 51, 0.25) 100%)`,
                                        },
                                    },
                                    '&.cancelled-past-due-row': {
                                        background: `linear-gradient(135deg, rgba(139, 0, 0, 0.15) 0%, rgba(165, 42, 42, 0.15) 100%)`,
                                        borderLeft: `4px solid #8B0000`,
                                        borderTop: `1px solid rgba(139, 0, 0, 0.3)`,
                                        borderBottom: `1px solid rgba(139, 0, 0, 0.3)`,
                                        '&:hover': {
                                            background: `linear-gradient(135deg, rgba(139, 0, 0, 0.25) 0%, rgba(165, 42, 42, 0.25) 100%)`,
                                        },
                                    },
                                    '&.amount-due-row': {
                                        backgroundColor: `${theme.palette.error.light}15`,
                                        borderLeft: `4px solid ${theme.palette.error.main}`,
                                        '&:hover': {
                                            backgroundColor: `${theme.palette.error.light}25`,
                                        },
                                    },
                                },
                                '& .MuiDataGrid-row.gold-past-due-row .MuiDataGrid-cell': {
                                    fontWeight: 600,
                                    color: '#000',
                                },
                                '& .MuiDataGrid-row.silver-past-due-row .MuiDataGrid-cell': {
                                    fontWeight: 600,
                                    color: '#000',
                                },
                                '& .MuiDataGrid-row.bronze-past-due-row .MuiDataGrid-cell': {
                                    fontWeight: 600,
                                    color: '#000',
                                },
                                '& .MuiDataGrid-row.cancelled-past-due-row .MuiDataGrid-cell': {
                                    fontWeight: 600,
                                    color: '#FFF',
                                },
                                '& .MuiDataGrid-row.amount-due-row .MuiDataGrid-cell': {
                                    fontWeight: 500,
                                },
                                '& .MuiDataGrid-cell.amount-due-cell': {
                                    fontWeight: 'bold',
                                    color: theme.palette.error.main,
                                },
                                '& .MuiDataGrid-cell': {
                                    fontSize: '0.875rem',
                                    padding: theme.spacing(1.5, 2),
                                },
                                '& .MuiDataGrid-columnGroupHeader, & .MuiDataGrid-columnHeader--filledGroup':
                                    {
                                        fontSize: '0.8125rem',
                                        fontWeight: 700,
                                        letterSpacing: '0.04em',
                                        lineHeight: 1.3,
                                        color: theme.palette.primary.dark,
                                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                                        padding: theme.spacing(1.5, 2.5),
                                        boxShadow: `inset 0 1px 0 ${alpha(
                                            theme.palette.common.black,
                                            0.05,
                                        )}`,
                                        borderLeft: `2px solid ${alpha(
                                            theme.palette.primary.main,
                                            0.5,
                                        )}`,
                                    },
                                '& .MuiDataGrid-columnGroupHeader .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-columnHeader--filledGroup .MuiDataGrid-columnHeaderTitle':
                                    {
                                        fontWeight: 700,
                                        color: theme.palette.primary.dark,
                                    },
                                '& .MuiDataGrid-columnHeader[data-field="subscription_level"], & .MuiDataGrid-columnHeader[data-field="uploads_last_30_days"], & .MuiDataGrid-columnHeader[data-field="total_commissions"], & .MuiDataGrid-columnHeader[data-field="pods_org"], & .MuiDataGrid-cell[data-field="subscription_level"], & .MuiDataGrid-cell[data-field="uploads_last_30_days"], & .MuiDataGrid-cell[data-field="total_commissions"], & .MuiDataGrid-cell[data-field="pods_org"]':
                                    {
                                        borderLeft: `2px solid ${alpha(
                                            theme.palette.primary.main,
                                            0.5,
                                        )}`,
                                        boxSizing: 'border-box',
                                    },
                                '& .MuiDataGrid-columnHeader': {
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                    backgroundColor: theme.palette.grey[50],
                                    padding: theme.spacing(1.5, 2),
                                },
                                '& .MuiDataGrid-columnHeader[data-field="amount_past_due"]': {
                                    backgroundColor: `${theme.palette.error.light}20`,
                                    borderBottom: `3px solid ${theme.palette.error.main}`,
                                },
                                '& .MuiDataGrid-columnHeaderTitle': {
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                },
                                '& .MuiDataGrid-columnHeader[data-field="amount_past_due"] .MuiDataGrid-columnHeaderTitle':
                                    {
                                        color: theme.palette.error.main,
                                        fontWeight: 700,
                                    },
                                '& .MuiDataGrid-cell.subscription-tier-gold': {
                                    backgroundColor: '#FFD700',
                                    fontWeight: 600,
                                    color: '#000',
                                },
                                '& .MuiDataGrid-cell.subscription-tier-silver': {
                                    backgroundColor: '#C0C0C0',
                                    fontWeight: 600,
                                    color: '#000',
                                },
                                '& .MuiDataGrid-cell.subscription-tier-bronze': {
                                    backgroundColor: '#CD7F32',
                                    fontWeight: 600,
                                    color: '#FFF',
                                },
                                '& .MuiDataGrid-footerContainer': {
                                    fontSize: '0.875rem',
                                    borderTop: `1px solid ${theme.palette.divider}`,
                                },
                                '& .MuiDataGrid-pinnedColumns': {
                                    backgroundColor: theme.palette.background.paper,
                                },
                                '& .MuiDataGrid-cell[data-field="name"]': {
                                    backgroundColor: theme.palette.background.paper,
                                    fontWeight: 500,
                                },
                                '& .MuiDataGrid-columnHeader[data-field="name"]': {
                                    backgroundColor: theme.palette.grey[100],
                                    fontWeight: 600,
                                },
                            }}
                        />
                    )}
                </Paper>
            </Box>
            <Dialog
                open={modalOpen}
                onClose={handleCloseModal}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        pb: 2,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.grey[50],
                    }}
                >
                    <Typography variant="h5" fontWeight={600}>
                        {selectedOrganization?.name || 'Organization Details'}
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    {selectedOrganization && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {/* Organization Info Card */}
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2.5,
                                    backgroundColor: theme.palette.grey[50],
                                    borderRadius: 2,
                                    border: `1px solid ${theme.palette.divider}`,
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        fontWeight: 600,
                                        color: 'text.secondary',
                                        mb: 1,
                                        display: 'block',
                                    }}
                                >
                                    Organization
                                </Typography>
                                <Typography variant="h6" fontWeight={600}>
                                    {selectedOrganization.name}
                                </Typography>
                            </Paper>

                            {selectedOrganization.creator && (
                                <>
                                    {/* Contact Name */}
                                    <Box>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px',
                                                fontWeight: 600,
                                                color: 'text.secondary',
                                                mb: 1,
                                                display: 'block',
                                            }}
                                        >
                                            Contact Person
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            fontWeight={500}
                                            fontSize="1.1rem"
                                        >
                                            {(() => {
                                                const creator = selectedOrganization.creator as any;
                                                const firstName =
                                                    creator?.first_name ||
                                                    creator?.creator_first_name ||
                                                    '';
                                                const lastName =
                                                    creator?.last_name ||
                                                    creator?.creator_last_name ||
                                                    '';
                                                return `${firstName} ${lastName}`.trim() || 'N/A';
                                            })()}
                                        </Typography>
                                    </Box>

                                    <Divider />

                                    {/* Phone Number Section */}
                                    <Box>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px',
                                                fontWeight: 600,
                                                color: 'text.secondary',
                                                mb: 2,
                                                display: 'block',
                                            }}
                                        >
                                            Phone Number
                                        </Typography>
                                        {(() => {
                                            const creator = selectedOrganization.creator as any;
                                            const phone =
                                                creator?.phone ||
                                                creator?.creator_phone ||
                                                creator?.phone_number ||
                                                creator?.user_phone ||
                                                null;
                                            return phone ? (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: 2,
                                                    }}
                                                >
                                                    <Paper
                                                        elevation={0}
                                                        sx={{
                                                            p: 2,
                                                            backgroundColor: `${theme.palette.primary.light}08`,
                                                            borderRadius: 2,
                                                            border: `1px solid ${theme.palette.primary.light}40`,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="h6"
                                                            sx={{
                                                                fontFamily: 'monospace',
                                                                fontWeight: 600,
                                                                color: 'text.primary',
                                                                mb: 2,
                                                                textAlign: 'center',
                                                            }}
                                                        >
                                                            {phone}
                                                        </Typography>
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                gap: 1.5,
                                                                justifyContent: 'center',
                                                            }}
                                                        >
                                                            <Button
                                                                variant="contained"
                                                                startIcon={<VideoCallIcon />}
                                                                onClick={() =>
                                                                    handleFaceTimeClick(phone)
                                                                }
                                                                sx={{
                                                                    minWidth: 140,
                                                                    textTransform: 'none',
                                                                    fontWeight: 600,
                                                                    boxShadow: 2,
                                                                }}
                                                            >
                                                                Call
                                                            </Button>
                                                            <Button
                                                                variant="contained"
                                                                color="secondary"
                                                                startIcon={<MessageIcon />}
                                                                onClick={() =>
                                                                    handleIMessageClick(phone)
                                                                }
                                                                sx={{
                                                                    minWidth: 140,
                                                                    textTransform: 'none',
                                                                    fontWeight: 600,
                                                                    boxShadow: 2,
                                                                }}
                                                            >
                                                                Text
                                                            </Button>
                                                        </Box>
                                                    </Paper>
                                                </Box>
                                            ) : (
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    fontStyle="italic"
                                                >
                                                    No phone number available
                                                </Typography>
                                            );
                                        })()}
                                    </Box>

                                    <Divider />

                                    {/* Email Section */}
                                    <Box>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px',
                                                fontWeight: 600,
                                                color: 'text.secondary',
                                                mb: 2,
                                                display: 'block',
                                            }}
                                        >
                                            Email Address
                                        </Typography>
                                        {(() => {
                                            const creator = selectedOrganization.creator as any;
                                            const email =
                                                creator?.email ||
                                                creator?.creator_email ||
                                                creator?.email_address ||
                                                creator?.user_email ||
                                                null;
                                            return email ? (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: 2,
                                                    }}
                                                >
                                                    <Paper
                                                        elevation={0}
                                                        sx={{
                                                            p: 2,
                                                            backgroundColor: `${theme.palette.info.light}08`,
                                                            borderRadius: 2,
                                                            border: `1px solid ${theme.palette.info.light}40`,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="body1"
                                                            sx={{
                                                                fontFamily: 'monospace',
                                                                fontWeight: 500,
                                                                color: 'text.primary',
                                                                mb: 2,
                                                                textAlign: 'center',
                                                                wordBreak: 'break-word',
                                                            }}
                                                        >
                                                            {email}
                                                        </Typography>
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                justifyContent: 'center',
                                                            }}
                                                        >
                                                            <Button
                                                                variant="contained"
                                                                color="info"
                                                                startIcon={<EmailIcon />}
                                                                onClick={() => {
                                                                    window.location.href = `mailto:${email}`;
                                                                }}
                                                                sx={{
                                                                    minWidth: 180,
                                                                    textTransform: 'none',
                                                                    fontWeight: 600,
                                                                    boxShadow: 2,
                                                                }}
                                                            >
                                                                Send Email
                                                            </Button>
                                                        </Box>
                                                    </Paper>
                                                </Box>
                                            ) : (
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    fontStyle="italic"
                                                >
                                                    No email address available
                                                </Typography>
                                            );
                                        })()}
                                    </Box>
                                </>
                            )}
                            {!selectedOrganization.creator && (
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 3,
                                        backgroundColor: `${theme.palette.warning.light}08`,
                                        borderRadius: 2,
                                        border: `1px solid ${theme.palette.warning.light}40`,
                                        textAlign: 'center',
                                    }}
                                >
                                    <InfoIcon
                                        sx={{
                                            fontSize: 40,
                                            color: 'text.secondary',
                                            mb: 1,
                                        }}
                                    />
                                    <Typography variant="body2" color="text.secondary">
                                        No contact information available for this organization
                                    </Typography>
                                </Paper>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions
                    sx={{
                        px: 3,
                        py: 2,
                        borderTop: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.grey[50],
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={handleCloseModal}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            minWidth: 100,
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default DashboardMetricsScene;
